import {getRandomColor} from "../../../utils/GetRandomColor";
import bringingAGunToATrainerFight from "../../../assets/json/4.2-bringing-a-gun-to-a-trainer-fight.json";
import {Player} from "@lottiefiles/react-lottie-player";
import IconText from "../../IconText";
import {faGun} from "@fortawesome/free-solid-svg-icons";

export default function BringingAGunToATrainerFight() {
    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    return (
        <div className={"blog-body"}>
            <h1>
            <IconText icon={faGun} text={"4.2. Bringing a Gun to a Trainer Fight"}/>
            </h1>

            <h2>Question</h2>
            <div className={"blog-quote"}>
                <p>
                    Uh-oh -- you've been cornered by one of Commander Lambdas elite bunny trainers! Fortunately, you
                    grabbed
                    a beam weapon from an abandoned storeroom while you were running through the station, so you have a
                    chance to fight your way out. But the beam weapon is potentially dangerous to you as well as to the
                    bunny trainers: its beams reflect off walls, meaning you'll have to be very careful where you shoot
                    to
                    avoid bouncing a shot toward yourself!
                </p>
                <p>
                    Luckily, the beams can only travel a certain maximum distance before becoming too weak to cause
                    damage.
                    You also know that if a beam hits a corner, it will bounce back in exactly the same direction. And
                    of
                    course, if the beam hits either you or the bunny trainer, it will stop immediately (albeit
                    painfully).
                </p>
                <p>
                    Write a function solution(dimensions, your_position, trainer_position, distance) that gives an array
                    of
                    2 integers of the width and height of the room, an array of 2 integers of your x and y coordinates
                    in
                    the room, an array of 2 integers of the trainer's x and y coordinates in the room, and returns an
                    integer of the number of distinct directions that you can fire to hit the elite trainer, given the
                    maximum distance that the beam can travel.
                </p>
                <p>
                    The room has integer dimensions [1 &lt; x_dim &lt;=1250, 1 &lt; y_dim &lt;=1250]. You and the elite
                    trainer are both
                    positioned on the integer lattice at different distinct positions (x, y) inside the room such that
                    [0 &lt; x &lt; x_dim, 0 &lt; y &lt; y_dim]. Finally, the maximum distance that the beam can travel
                    before
                    becoming harmless will be given as an integer 1 &lt; distance &lt;=10000. </p>
                <p>
                    For example, if you and the elite trainer were positioned in a room with dimensions [3, 2],
                    your_position [1, 1], trainer_position [2, 1], and a maximum shot distance of 4, you could shoot
                    in seven different directions to hit the elite trainer (given as vector bearings from your
                    location): [1, 0], [1, 2], [1, -2], [3, 2], [3, -2], [-3, 2], and [-3, -2]. As specific
                    examples, the shot at bearing [1, 0] is the straight line horizontal shot of distance 1, the
                    shot at bearing [-3, -2] bounces off the left wall and then the bottom wall before hitting the
                    elite trainer with a total shot distance of sqrt(13), and the shot at bearing [1, 2] bounces off
                    just the top wall before hitting the elite trainer with a total shot distance of sqrt(5).
                </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution([300,275], [150,150], [185,100], 500) <br/>
                    Output: <br/>
                    9 <br/>
                    <br/>
                    Input: <br/>
                    solution.solution([3,2], [1,1], [2,1], 4) <br/>
                    Output: <br/>
                    7 <br/>
                </p>
            </div>

            <h2>Solution</h2>
            <p>
                Thanks to the optics lectures in high school, figuring this out took me only a few minutes, but coding
                the
                solution wasn't that easy. The idea is to find the reflection points of the trainer and, depending on
                the
                range of the beam, find the possible shots to hit the trainer.
            </p>
            <p>
                Like in the example given in the question, assume that you are on the point (1, 1), the trainer is on
                the
                point (2, 1), and the room is 3x2. When you find the reflections and draw a circle around yourself with
                a
                radius of 4, you will notice that there are 9 reflection points of the trainer inside the circle.
            </p>
            <p>
                One thing to consider here is if there are any reflections of yourself between you and the trainer's
                reflection points. If there are, you should eliminate those shots, since the beam will hit you before
                hitting the trainer by bouncing off the walls.
            </p>
            <p>
                Another one is to check if there are any reflections behind the trainer and the trainer's reflection
                points.
                The question is asking how many distinct directions you can fire to hit the trainer, so counting the
                reflections behind the trainer or the trainer's reflection points leads to the wrong results.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={bringingAGunToATrainerFight} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>
            
            <p>
                In our example, there are 2 reflection points that satisfy the conditions above. So the answer is 9-2=7.
            </p>
            <p>
                I spent almost all of my time on this question, optimizing my approach. You will notice the helper
                functions
                <code>calculate_angle_and_distance</code> and <code>calculate_reflection_points</code> in the code,
                which
                serve the purpose explained
                above.
            </p>

            <h2>Code</h2>
            <pre><code className="language-python">{`from math import sqrt, atan2

def calculate_angle_and_distance(point):
    '''
    Returns the angle and distance of the point from the origin.
    '''
    x, y = point
    return atan2(y, x), sqrt(x**2 + y**2)

def calculate_reflection_points(the_point, dimensions, loop_count):
    '''
    Returns the reflection points of the given point depending on the dimensions and the loop count.
    Dimensions are the width and height of the room, assumed to have mirrored walls.
    Loop count is the number of times the room is mirrored.
    '''
    reflection_points = {tuple(the_point)}
                max_x, max_y = dimensions
    new_reflection_points = set()

    for _ in range(loop_count):
        for point in reflection_points:
            x, y = point
            reflections = {(x, 2 * max_y - y), (2 * max_x - x, y), (2 * max_x - x, 2 * max_y - y)}
                new_reflection_points.update(reflections - reflection_points)
        max_x *= 2
        max_y *= 2
        reflection_points.update(new_reflection_points)
        new_reflection_points.clear()

    final_reflection_points = set()
    for point in reflection_points:
        x, y = point
        final_reflection_points.update({(x, y), (-x, y), (x, -y), (-x, -y)})

    return final_reflection_points

def solution(dimensions, your_position, trainer_position, distance):
    loop_count = min(distance // min(dimensions), 9)
    trainer_reflection_points = calculate_reflection_points(trainer_position, dimensions, loop_count)

    vectors = [(point[0] - your_position[0], point[1] - your_position[1]) for point in trainer_reflection_points]
    possible_shots = [(x, y) for x, y in vectors if sqrt(x**2 + y**2) < distance]

                # Calculate angle and distance for each shot
    shots_with_angle_and_distance = [(calculate_angle_and_distance(shot), shot) for shot in possible_shots]

    # Sort shots by angle and then by distance
    shots_with_angle_and_distance.sort()

    # Remove shots that have the same angle and a greater distance
    previous_angle = None
    shots_to_keep = []
    for (angle, distance), shot in shots_with_angle_and_distance:
        if angle != previous_angle:
            shots_to_keep.append(shot)
            previous_angle = angle

    possible_shots = shots_to_keep

    your_reflection_points = calculate_reflection_points(your_position, dimensions, loop_count)
    your_reflection_vectors = [(point[0] - your_position[0], point[1] - your_position[1]) for point in your_reflection_points]

    max_x = max(point[0] for point in possible_shots)
    min_x = min(point[0] for point in possible_shots)
    max_y = max(point[1] for point in possible_shots)
    min_y = min(point[1] for point in possible_shots)

    your_reflection_vectors = [vector for vector in your_reflection_vectors if min_x < vector[0] < max_x and min_y
                < vector[1] < max_y]
                origin = (0, 0)
    if origin in your_reflection_vectors:
        your_reflection_vectors.remove(origin)

    reflection_angles_distances = [calculate_angle_and_distance(reflection) for reflection in your_reflection_vectors]

    valid_shots = []

    for point in possible_shots:
        shot_angle, shot_distance = calculate_angle_and_distance(point)

        if any(angle == shot_angle and distance < shot_distance htmlFor angle, distance in reflection_angles_distances):
            continue

        valid_shots.append(point)

    possible_shots = valid_shots

    return len(possible_shots)`}</code></pre>
        </div>
    );
}